import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    TextField,
    Button,
    Alert,
    CircularProgress,
    Stepper,
    Step,
    StepLabel,
    Card,
    CardContent,
    CardActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    Chip
} from '@mui/material';
import {
    Send as SendIcon,
    Preview as PreviewIcon,
    ArrowBack as BackIcon,
    ArrowForward as NextIcon,
    AttachFile as AttachIcon,
    Delete as DeleteIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const steps = ['Select Template', 'Fill Customer Data', 'Preview & Send'];

function SendOffer() {
    const { currentUser } = useAuth();
    const [activeStep, setActiveStep] = useState(0);
    const [templates, setTemplates] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [placeholders, setPlaceholders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [emailSent, setEmailSent] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewContent, setPreviewContent] = useState({ subject: '', htmlContent: '' });
    const [attachments, setAttachments] = useState([]);
    const [validationErrors, setValidationErrors] = useState({});
    const [internetFilter, setInternetFilter] = useState('all'); // all, 250mb, 1gb, 3gb
    
    const [customerData, setCustomerData] = useState({
        customer_first_name: '',
        customer_full_name: '',
        customer_email: '',
        customer_phone: '',
        agent_name: currentUser?.name || '',
        agent_phone: currentUser?.phone || ''
    });

    useEffect(() => {
        fetchTemplates();
    }, []);

    // Filter templates based on internet plan
    const getFilteredTemplates = () => {
        if (internetFilter === 'all') return templates;
        
        return templates.filter(template => {
            const name = template.name.toLowerCase();
            switch (internetFilter) {
                case '250mb':
                    return name.includes('250mb') || name.includes('250');
                case '1gb':
                    return name.includes('1gb');
                case '3gb':
                    return name.includes('3gb');
                default:
                    return true;
            }
        });
    };

    // Validation functions
    const isValidName = (name) => {
        const nameRegex = /^[a-zA-Z\s'-]+$/;
        return name.trim().length > 0 && nameRegex.test(name.trim());
    };

    const validateField = (fieldName, value) => {
        const errors = { ...validationErrors };
        
        switch (fieldName) {
            case 'customer_first_name':
                if (!value.trim()) {
                    errors.customer_first_name = 'First name is required';
                } else if (!isValidName(value)) {
                    errors.customer_first_name = 'First name must contain only letters, spaces, hyphens, and apostrophes';
                } else {
                    delete errors.customer_first_name;
                }
                break;
            case 'customer_full_name':
                if (!value.trim()) {
                    errors.customer_full_name = 'Full name is required';
                } else if (!isValidName(value)) {
                    errors.customer_full_name = 'Full name must contain only letters, spaces, hyphens, and apostrophes';
                } else {
                    delete errors.customer_full_name;
                }
                break;
            case 'customer_email':
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!value.trim()) {
                    errors.customer_email = 'Email is required';
                } else if (!emailRegex.test(value)) {
                    errors.customer_email = 'Please enter a valid email address';
                } else {
                    delete errors.customer_email;
                }
                break;
            case 'customer_phone':
                if (!value.trim()) {
                    errors.customer_phone = 'Phone number is required';
                } else if (value.length !== 10) {
                    errors.customer_phone = 'Phone number must be exactly 10 digits';
                } else {
                    delete errors.customer_phone;
                }
                break;
        }
        
        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const fetchTemplates = async () => {
        try {
            setLoading(true);
            const response = await axios.get('/api/templates');
            setTemplates(response.data.templates);
        } catch (error) {
            setError('Failed to fetch templates');
        } finally {
            setLoading(false);
        }
    };

    const handleTemplateSelect = async (templateId) => {
        try {
            setLoading(true);
            setError('');
            
            const response = await axios.get(`/api/templates/${templateId}`);
            setSelectedTemplate(response.data.template);
            
            // Extract placeholders
            const placeholderResponse = await axios.post(`/api/templates/${templateId}/extract-placeholders`);
            setPlaceholders(placeholderResponse.data.placeholders);
            
            setActiveStep(1);
        } catch (error) {
            setError('Failed to load template');
        } finally {
            setLoading(false);
        }
    };

    const handleNext = () => {
        if (activeStep === 1) {
            // Validate all customer data fields
            const fieldsToValidate = ['customer_first_name', 'customer_full_name', 'customer_email', 'customer_phone'];
            let hasErrors = false;
            
            fieldsToValidate.forEach(field => {
                const isValid = validateField(field, customerData[field]);
                if (!isValid) hasErrors = true;
            });
            
            if (hasErrors) {
                setError('Please fix the errors above before continuing');
                return;
            }
            
            setError('');
        }
        setActiveStep((prev) => prev + 1);
    };

    const handleBack = () => {
        setActiveStep((prev) => prev - 1);
    };

    const handlePreview = async () => {
        try {
            setLoading(true);
            const response = await axios.post(`/api/templates/${selectedTemplate.id}/generate-filled`, {
                values: customerData
            });
            setPreviewContent(response.data);
            setPreviewOpen(true);
        } catch (error) {
            setError('Failed to generate preview');
        } finally {
            setLoading(false);
        }
    };

    const handleFileUpload = (event) => {
        const files = Array.from(event.target.files);
        
        // Validate file count
        if (attachments.length + files.length > 5) {
            setError('Maximum 5 files allowed per email');
            return;
        }
        
        // Validate file sizes (25MB total)
        const totalSize = [...attachments, ...files].reduce((sum, file) => sum + file.size, 0);
        if (totalSize > 25 * 1024 * 1024) {
            setError('Total file size cannot exceed 25MB');
            return;
        }
        
        setAttachments(prev => [...prev, ...files]);
        setError('');
        
        // Clear the input
        event.target.value = '';
    };
    
    const handleRemoveAttachment = (index) => {
        setAttachments(prev => prev.filter((_, i) => i !== index));
    };
    
    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const handleSend = async () => {
        try {
            setSending(true);
            setError('');
            
            // Create FormData for file upload
            const formData = new FormData();
            formData.append('templateId', selectedTemplate.id);
            formData.append('customerData', JSON.stringify(customerData));
            
            // Add attachments
            attachments.forEach(file => {
                formData.append('attachments', file);
            });
            
            await axios.post('/api/email/send', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            
            setEmailSent(true);
            setSuccess(`Email sent successfully${attachments.length > 0 ? ` with ${attachments.length} attachment(s)` : ''}!`);
            
            // Reset form
            setTimeout(() => {
                setActiveStep(0);
                setSelectedTemplate(null);
                setAttachments([]);
                setEmailSent(false);
                setCustomerData({
                    customer_first_name: '',
                    customer_full_name: '',
                    customer_email: '',
                    customer_phone: '',
                    agent_name: currentUser?.name || '',
                    agent_phone: currentUser?.phone || ''
                });
                setSuccess('');
            }, 3000);
            
        } catch (error) {
            setError(error.response?.data?.error || 'Failed to send email');
        } finally {
            setSending(false);
        }
    };

    const renderStepContent = () => {
        switch (activeStep) {
            case 0:
                return (
                    <Box>
                        <Typography variant="h6" gutterBottom>
                            Select an Email Template
                        </Typography>
                        
                        {/* Internet Plan Filter */}
                        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                <Button
                                    variant={internetFilter === 'all' ? 'contained' : 'outlined'}
                                    size="small"
                                    onClick={() => setInternetFilter('all')}
                                >
                                    All Plans ({templates.length})
                                </Button>
                                <Button
                                    variant={internetFilter === '250mb' ? 'contained' : 'outlined'}
                                    size="small"
                                    onClick={() => setInternetFilter('250mb')}
                                >
                                    250MB Plans ({templates.filter(t => t.name.toLowerCase().includes('250mb') || t.name.toLowerCase().includes('250')).length})
                                </Button>
                                <Button
                                    variant={internetFilter === '1gb' ? 'contained' : 'outlined'}
                                    size="small"
                                    onClick={() => setInternetFilter('1gb')}
                                >
                                    1GB Plans ({templates.filter(t => t.name.toLowerCase().includes('1gb')).length})
                                </Button>
                                <Button
                                    variant={internetFilter === '3gb' ? 'contained' : 'outlined'}
                                    size="small"
                                    onClick={() => setInternetFilter('3gb')}
                                >
                                    3GB Plans ({templates.filter(t => t.name.toLowerCase().includes('3gb')).length})
                                </Button>
                            </Box>
                        </Box>
                        
                        {loading ? (
                            <Box display="flex" justifyContent="center" mt={4}>
                                <CircularProgress />
                            </Box>
                        ) : (
                            <Grid container spacing={2} sx={{ mt: 1 }}>
                                {getFilteredTemplates().map((template) => (
                                    <Grid item xs={12} sm={6} md={4} lg={3} key={template.id}>
                                        <Card 
                                            sx={{ 
                                                height: '100%',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                cursor: 'pointer',
                                                transition: 'all 0.3s ease',
                                                '&:hover': { 
                                                    boxShadow: 4,
                                                    transform: 'translateY(-2px)',
                                                    backgroundColor: 'action.hover'
                                                }
                                            }}
                                            onClick={() => handleTemplateSelect(template.id)}
                                        >
                                            <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                                                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                                                    <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
                                                        {template.name}
                                                    </Typography>
                                                    <Chip 
                                                        label={`#${template.sortOrder || 999}`} 
                                                        size="small" 
                                                        color="primary" 
                                                        variant="outlined"
                                                    />
                                                </Box>
                                                <Typography 
                                                    variant="body2" 
                                                    color="text.secondary" 
                                                    sx={{ 
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: 2,
                                                        WebkitBoxOrient: 'vertical',
                                                        overflow: 'hidden',
                                                        mb: 1,
                                                        minHeight: '2.4em'
                                                    }}
                                                >
                                                    {template.subjectLine}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    Created: {new Date(template.createdAt).toLocaleDateString()}
                                                </Typography>
                                            </CardContent>
                                            <CardActions sx={{ pt: 0, justifyContent: 'center' }}>
                                                <Button size="small" variant="outlined">
                                                    Select Template
                                                </Button>
                                            </CardActions>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>
                        )}
                    </Box>
                );

            case 1:
                return (
                    <Box>
                        <Typography variant="h6" gutterBottom>
                            Fill Customer Information
                        </Typography>
                        <Typography variant="body2" color="text.secondary" paragraph>
                            Template: {selectedTemplate?.name}
                        </Typography>

                        <Box sx={{ display: 'grid', gap: 2, mt: 2 }}>
                            <TextField
                                fullWidth
                                label="Customer First Name"
                                value={customerData.customer_first_name}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setCustomerData({ 
                                        ...customerData, 
                                        customer_first_name: value 
                                    });
                                    validateField('customer_first_name', value);
                                }}
                                required
                                error={!!validationErrors.customer_first_name}
                                helperText={validationErrors.customer_first_name || "Letters, spaces, hyphens, and apostrophes only"}
                            />
                            <TextField
                                fullWidth
                                label="Customer Full Name"
                                value={customerData.customer_full_name}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setCustomerData({ 
                                        ...customerData, 
                                        customer_full_name: value 
                                    });
                                    validateField('customer_full_name', value);
                                }}
                                required
                                error={!!validationErrors.customer_full_name}
                                helperText={validationErrors.customer_full_name || "Letters, spaces, hyphens, and apostrophes only"}
                            />
                            <TextField
                                fullWidth
                                label="Customer Email"
                                type="email"
                                value={customerData.customer_email}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setCustomerData({ 
                                        ...customerData, 
                                        customer_email: value 
                                    });
                                    validateField('customer_email', value);
                                }}
                                required
                                error={!!validationErrors.customer_email}
                                helperText={validationErrors.customer_email || "Valid email address"}
                            />
                            <TextField
                                fullWidth
                                label="Customer Phone"
                                value={customerData.customer_phone}
                                onChange={(e) => {
                                    const value = e.target.value.replace(/\D/g, ''); // Remove non-digits
                                    if (value.length <= 10) {
                                        setCustomerData({ 
                                            ...customerData, 
                                            customer_phone: value 
                                        });
                                        validateField('customer_phone', value);
                                    }
                                }}
                                required
                                error={!!validationErrors.customer_phone}
                                inputProps={{ 
                                    maxLength: 10,
                                    pattern: '[0-9]{10}',
                                    inputMode: 'numeric'
                                }}
                                helperText={validationErrors.customer_phone || "10 digits only (e.g., 5551234567)"}
                            />
                            
                            <Divider sx={{ my: 2 }} />
                            <Typography variant="h6">Agent Information</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                This information is automatically filled from your profile
                            </Typography>
                            
                            <TextField
                                fullWidth
                                label="Your Name"
                                value={customerData.agent_name}
                                disabled
                                sx={{ 
                                    mb: 2,
                                    '& .MuiInputBase-input.Mui-disabled': {
                                        color: 'text.primary',
                                        WebkitTextFillColor: 'unset'
                                    }
                                }}
                                helperText="From your account profile"
                            />
                            <TextField
                                fullWidth
                                label="Your Phone"
                                value={customerData.agent_phone || 'Not provided in profile'}
                                disabled
                                sx={{ 
                                    '& .MuiInputBase-input.Mui-disabled': {
                                        color: 'text.primary',
                                        WebkitTextFillColor: 'unset'
                                    }
                                }}
                                helperText="From your account profile"
                            />
                        </Box>
                        
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="h6">File Attachments (Optional)</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            You can attach up to 5 files with a total size limit of 25MB
                        </Typography>
                        
                        <Box sx={{ mb: 2 }}>
                            <input
                                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.jpg,.jpeg,.png,.gif,.webp,.zip"
                                style={{ display: 'none' }}
                                id="file-upload"
                                type="file"
                                multiple
                                onChange={handleFileUpload}
                            />
                            <label htmlFor="file-upload">
                                <Button
                                    variant="outlined"
                                    component="span"
                                    startIcon={<AttachIcon />}
                                    disabled={attachments.length >= 5}
                                >
                                    Attach Files
                                </Button>
                            </label>
                            <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                                Supported: PDF, Word, Excel, PowerPoint, Images, Text files, ZIP
                            </Typography>
                        </Box>
                        
                        {attachments.length > 0 && (
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    Attached Files ({attachments.length}/5):
                                </Typography>
                                {attachments.map((file, index) => (
                                    <Box 
                                        key={index} 
                                        sx={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            justifyContent: 'space-between',
                                            p: 1, 
                                            border: '1px solid', 
                                            borderColor: 'divider',
                                            borderRadius: 1,
                                            mb: 1 
                                        }}
                                    >
                                        <Box>
                                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                                {file.name}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {formatFileSize(file.size)}
                                            </Typography>
                                        </Box>
                                        <Button
                                            size="small"
                                            color="error"
                                            onClick={() => handleRemoveAttachment(index)}
                                            startIcon={<DeleteIcon />}
                                        >
                                            Remove
                                        </Button>
                                    </Box>
                                ))}
                                <Typography variant="caption" color="text.secondary">
                                    Total size: {formatFileSize(attachments.reduce((sum, file) => sum + file.size, 0))}
                                </Typography>
                            </Box>
                        )}

                        {placeholders.length > 0 && (
                            <Alert severity="info" sx={{ mt: 2 }}>
                                This template uses the following placeholders: {placeholders.join(', ')}
                            </Alert>
                        )}
                    </Box>
                );

            case 2:
                return (
                    <Box>
                        <Typography variant="h6" gutterBottom>
                            Review and Send
                        </Typography>
                        
                        <Card sx={{ mb: 2 }}>
                            <CardContent>
                                <Typography variant="h6">Email Summary</Typography>
                                <Typography><strong>Template:</strong> {selectedTemplate?.name}</Typography>
                                <Typography><strong>To:</strong> {customerData.customer_full_name} &lt;{customerData.customer_email}&gt;</Typography>
                                <Typography><strong>From:</strong> {customerData.agent_name}</Typography>
                                {attachments.length > 0 && (
                                    <Typography><strong>Attachments:</strong> {attachments.length} file(s) ({formatFileSize(attachments.reduce((sum, file) => sum + file.size, 0))})</Typography>
                                )}
                            </CardContent>
                        </Card>

                        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                            <Button
                                variant="outlined"
                                startIcon={<PreviewIcon />}
                                onClick={handlePreview}
                                disabled={loading}
                            >
                                Preview Email
                            </Button>
                        </Box>

                        {success && (
                            <Alert severity="success" sx={{ mb: 2 }}>
                                {success}
                            </Alert>
                        )}
                    </Box>
                );

            default:
                return null;
        }
    };

    return (
        <Box>
            <Typography variant="h4" gutterBottom>
                Send Offer
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <Paper sx={{ p: 3 }}>
                <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
                    {steps.map((label) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>

                {renderStepContent()}

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                    <Button
                        disabled={activeStep === 0}
                        onClick={handleBack}
                        startIcon={<BackIcon />}
                    >
                        Back
                    </Button>

                    <Box>
                        {activeStep < steps.length - 1 ? (
                            <Button
                                variant="contained"
                                onClick={handleNext}
                                endIcon={<NextIcon />}
                                disabled={!selectedTemplate && activeStep === 0}
                            >
                                Next
                            </Button>
                        ) : (
                            <Button
                                variant="contained"
                                color="success"
                                onClick={handleSend}
                                disabled={sending || emailSent}
                                startIcon={sending ? <CircularProgress size={20} /> : <SendIcon />}
                            >
                                {sending ? 'Sending...' : emailSent ? 'Email Sent ✓' : 'Send Email'}
                            </Button>
                        )}
                    </Box>
                </Box>
            </Paper>

            {/* Preview Dialog */}
            <Dialog 
                open={previewOpen} 
                onClose={() => setPreviewOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Email Preview</DialogTitle>
                <DialogContent>
                    <Typography variant="h6" gutterBottom>
                        Subject: {previewContent.subject}
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Box 
                        sx={{ 
                            border: '1px solid #ccc', 
                            p: 2, 
                            minHeight: 300,
                            backgroundColor: '#fff'
                        }}
                        dangerouslySetInnerHTML={{ __html: previewContent.htmlContent }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPreviewOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default SendOffer;